import { useEffect, useRef, useState } from 'react';
import { GAME_HAND, HAND_TYPE } from 'constants/gameConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useTableSelector } from 'hooks/useTableSelector';

// Mirror the WinChips.tsx flight so the stack sums UP in step with the chips
// crossing the table (point A → point B), not before them. Per chip there:
//   lift off the pot (0.2s) → fly to the seat (0.5s) → fade; and the per-chip
//   stagger (i * 0.03 over 10 chips) spreads arrivals by ~0.27s.
// So chips stream into the seat from ~0.2s (they begin heading over) to ~0.95s
// (last one lands). The count-up holds through the lift, then climbs LINEARLY
// across that travel window so the number rises together with the chips and
// tops out exactly as the last one lands. Keep these in sync with WinChips.tsx.
const CHIP_LIFT_SEC = 0.2; // chips pop up at the pot before heading to the seat
const CHIP_TRAVEL_SEC = 0.5; // pot → seat flight time per chip
const CHIP_STAGGER_SEC = 0.27; // spread between the first and last chip landing
const WIN_COUNTUP_DELAY_SEC = CHIP_LIFT_SEC; // hold until chips leave for the seat
const WIN_COUNTUP_SEC = CHIP_TRAVEL_SEC + CHIP_STAGGER_SEC; // finish as the last lands

/**
 * Total winnings this seat is being awarded, summed across pots up to the
 * current one — read straight from `pots[i].winners`, the exact data the
 * pot→seat chip animation flies from.
 *
 * Gated on `currentStep >= 3` to match EXACTLY when Pot.tsx mounts WinChips
 * (`currentStep < 3` → no chips). The winners data lands a
 * few showdown-steps BEFORE the chips launch, so without this gate the count-up
 * starts early and runs ahead of the chips. With it, both fire on the same frame.
 *
 * Uses INDEX access (`pots[i]`) like playerPotWin / Cash, NOT Array iteration —
 * the engine ships `pots` as a numeric-keyed object, so `Array.isArray`/`for..of`
 * would wrongly see it as empty and return 0. Exported for unit testing.
 *
 * Hi-Lo phasing: a single pot is awarded HIGH first, then LOW (the engine flips
 * `showdown.handType` 'Hi'→'Lo' on the SAME currentPot). The current pot's win
 * must therefore be counted PER PHASE — otherwise, when one seat scoops both
 * halves, the stack jumps to hi+low during the HIGH animation before the LOW
 * chips even fly. So for the current pot we only add: the current phase's
 * portion, the HIGH portion once we've advanced to the LOW phase (it was already
 * paid), and any non-Hi-Lo (regular) win. Pots BEFORE the current one are fully
 * settled (both halves), so they count in full.
 */
export function seatWinTotal(pots: any, showdown: any, seatId: number): number {
  const currentPot = showdown?.currentPot;
  if (!pots || currentPot === undefined || currentPot === null) return 0;
  // Chips haven't launched yet (pot is mid-reveal) → stack must not climb.
  // Unconditional on the pot existing, matching Pot.tsx: a missing pot is not a
  // reason to start counting up early.
  if (showdown.currentStep < 3) return 0;

  const phase = showdown?.handType;
  let total = 0;
  for (let i = 0; i <= +currentPot; i++) {
    const winners = pots[i]?.winners?.filter((f: any) => +f.player === +seatId);
    if (!winners?.length) continue;
    if (i < +currentPot) {
      // Fully-settled earlier pot — both Hi and Lo already awarded.
      total += winners.map((f: any) => +f.win).reduce((t: number, n: number) => t + n, 0);
      continue;
    }
    // Current pot — award per Hi/Lo phase (currentStep >= 3 guaranteed above).
    const isHiLoPhase = phase === HAND_TYPE.HI || phase === HAND_TYPE.LO;
    for (const f of winners) {
      const counted =
        !isHiLoPhase || // not a phased Hi-Lo showdown → count the whole win
        !f.handType || // regular (non-Hi-Lo) pot entry
        f.handType === phase || // the phase being awarded now
        (f.handType === HAND_TYPE.HI && phase === HAND_TYPE.LO); // Hi paid, now on Lo
      if (counted) total += +f.win;
    }
  }
  return total;
}

/**
 * Wraps a seat's resolved stack (from useDisplayedStack) and returns a value that
 * COUNTS UP to its post-win total in step with the chips animation, instead of
 * jumping after it. Purely a display animation — it never mutates the stack or
 * touches the chip flight.
 *
 * It latches the FULL win target the instant the winners data appears and holds
 * it (a high-water mark) until the server commits the new stack, so the number
 * can't dip to $0 between "winners cleared" and "stack committed" (the all-in /
 * all-fold case). The latch is dropped once out of the showdown/payout phase so
 * it never leaks into the next hand.
 */
export function useAnimatedWinStack(
  seatId: number,
  baseStack: number | string | undefined
): number | undefined {
  const pots = useTableSelector((table) => table.game.pots);
  const showdown = useTableSelector((table) => table.game.showdown);
  const handNum = Number(useTableSelector((table) => table.game.hand));
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const win = seatWinTotal(pots, showdown, seatId);

  // The resolved stack arrives as a numeric STRING (e.g. "1.46") from the engine,
  // so coerce before any arithmetic — otherwise `base + win` concatenates
  // ("1.46" + 0.06 → "1.460.06") and Math.max() yields NaN, formatting to $0.
  const base =
    baseStack === undefined || baseStack === null || baseStack === ''
      ? undefined
      : Number(baseStack);
  const hasBase = base !== undefined && Number.isFinite(base);

  const [displayed, setDisplayed] = useState<number | undefined>(hasBase ? base : undefined);
  const proxyRef = useRef({ v: hasBase ? (base as number) : 0 });
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const latchRef = useRef<number | null>(null);

  const inShowdown = Number.isFinite(handNum) && handNum >= GAME_HAND.AFTER_RIVER_BETTING_ROUND;

  let target: number | undefined;
  if (!hasBase) {
    target = undefined;
  } else {
    const baseNum = base as number;
    if (win > 0) {
      // Winners exist (chips flying) → latch the full post-win total.
      const full = baseNum + win;
      latchRef.current = latchRef.current === null ? full : Math.max(latchRef.current, full);
    } else if (!inShowdown) {
      // Next hand has begun → drop any stale latch.
      latchRef.current = null;
    } else if (latchRef.current !== null && baseNum >= latchRef.current) {
      // Server committed the stack → release.
      latchRef.current = null;
    }
    target = latchRef.current !== null ? Math.max(latchRef.current, baseNum) : baseNum;
  }

  useEffect(() => {
    if (target === undefined) {
      setDisplayed(undefined);
      return undefined;
    }

    if (tweenRef.current) tweenRef.current.kill();

    const goingUp = target > proxyRef.current.v + 0.001;
    if (isHandHistorySnapshot) {
      proxyRef.current.v = target;
      setDisplayed(target);
      return undefined;
    }

    // Only the win award counts up (in step with the chips); everything else
    // (bets, normal updates, decreases) jumps instantly so play is unaffected.
    if (!goingUp || latchRef.current === null) {
      proxyRef.current.v = target;
      setDisplayed(target);
      return undefined;
    }

    tweenRef.current = gsap.to(proxyRef.current, {
      v: target,
      duration: WIN_COUNTUP_SEC,
      delay: WIN_COUNTUP_DELAY_SEC,
      ease: 'none',
      snap: { v: 0.01 },
      onUpdate: () => setDisplayed(proxyRef.current.v),
      onComplete: () => setDisplayed(target)
    });

    return () => {
      if (tweenRef.current) tweenRef.current.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandHistorySnapshot, target]);

  // A snapshot shows the resolved stack on its first render, not after the
  // passive effect above has caught `displayed` up from the base stack.
  return isHandHistorySnapshot ? target : displayed;
}
